import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CoinsIcon, TrophyIcon, CheckIcon, TrendingUpIcon, ActivityIcon, BarChart3Icon, HeartIcon, MessageSquareIcon, PenIcon, ClockIcon, ShieldCheckIcon, AlertCircleIcon } from 'lucide-react';
import Button from '@/components/form/Button';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';

export default function CashSnaps() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <Button
        variant="ghost"
        size="sm"
        icon={<ArrowLeftIcon className="h-4 w-4" />}
        onClick={() => navigate('/dashboard')}
        className="mb-6"
      >
        Back to Dashboard
      </Button>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
        <div className="px-6 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600">
          <h1 className="text-2xl font-bold text-white flex items-center">
            <CoinsIcon className="h-6 w-6 mr-2" />
            CashSNAPs & Conditions of Participation
          </h1>
        </div>

        {/* Early Adopter Benefits Section */}
          <section>
            <div className="p-4">
              <div className="bg-gradient-to-r from-yellow-100 to-yellow-200 p-4 rounded-xl mt-1">
                <h2 className="text-2xl font-bold text-yellow-800 mb-4 flex items-center justify-center">
                  <TrophyIcon className="h-8 w-8 mr-2 text-yellow-700" />
                  Early Adopter Benefits
                </h2>
                
                <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
                  <p className="text-gray-700 mb-4">
                    Join now during our launch phase (until May 31, 2025) and enjoy these exclusive benefits:
                  </p>
                  
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600 mt-0.5 mr-3">
                        <span className="text-lg font-bold">+</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">25% Activity Score Boost</h4>
                        <p className="text-gray-600 text-sm">All activity points you earn are automatically increased by 25%, giving you a significant advantage in prize distributions.</p>
                      </div>
                    </li>
  
                  </ul>
                </div>
              </div>
            </div>
          </section>

        <div className="p-4 space-y-10">
          {/* Age Verification Notice */}
          <div className="bg-red-50 border-l-4 border-red-500 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mt-1" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-red-800">Age Verification Required</h3>
                <div className="mt-2 text-red-700">
                  <p>You must be at least 18 years old to participate in CashSNAPs.</p>
                  <p className="mt-1">
                    Identity verification via Stripe is required in order to access and use CashSNAPs, in compliance with applicable legal regulations.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Introduction Section */}
          <section>
            <h2 className="text-2xl font-bold text-yellow-800 mb-4 flex items-center">
              <CoinsIcon className="h-10 w-10 mr-2 text-yellow-600" />
              What are CashSNAPs?
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              CashSNAPs are prediction challenges where you can win real cash prizes — completely free.
            </p>

            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <h3 className="text-xl font-semibold text-yellow-800 mb-4 flex items-center">
                <TrendingUpIcon className="h-5 w-5 mr-2 text-yellow-600" />
                How it works:
              </h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600">
                    <CoinsIcon className="h-5 w-5" />
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-medium text-yellow-800">Cash grows with votes</h4>
                    <p className="mt-1 text-gray-600">Every vote adds $0.05 to the prize pool (up to $500 per CASHSNAP).</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600">
                    <CheckIcon className="h-5 w-5" />
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-medium text-yellow-800">Make a prediction</h4>
                    <p className="mt-1 text-gray-600">Vote on the outcome of real-world events — like sports, politics, or society.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600">
                    <ClockIcon className="h-5 w-5" />
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-medium text-yellow-800">Early Voting Bonus</h4>
                    <p className="mt-1 text-gray-600">Vote early to maximize your potential winnings with our tiered bonus system:</p>
                    <ul className="mt-2 space-y-1 text-sm text-gray-600">
                      <li>• First 10% of duration: 100% of your activity score counts</li>
                      <li>• 10-30% of duration: 75% of your activity score counts</li>
                      <li>• 30-60% of duration: 50% of your activity score counts</li>
                      <li>• 60-90% of duration: 25% of your activity score counts</li>
                      <li>• Last 10% of duration: Only 5% of your activity score counts</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Eligibility Section */}
          <section>
            <h2 className="text-2xl font-bold text-yellow-800 mb-4 flex items-center">
              <ShieldCheckIcon className="h-5 w-5 mr-2 text-blue-600" />
              Eligibility
            </h2>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Age Requirement:</strong> Participants must be at least 18 years old.</li>
                <li><strong>Identity Verification:</strong> Withdrawal of winnings requires identity verification through Stripe.</li>
                <li><strong>One Account Policy:</strong> Only one account per individual is permitted.</li>
                <li><strong>Employee Exclusion:</strong> Employees of SnapVote.ai and their immediate families are not eligible.</li>
                <li><strong>Geographic Restrictions:</strong> Participation is open to residents of all countries where such contests are legal.</li>
                <li><strong>Legal Compliance:</strong> By participating, you confirm that you are not subject to any restrictions that would prevent you from receiving cash prizes.</li>
              </ul>
            </div>
          </section>

          {/* Prize Distribution Section */}
          <section>
            <h2 className="text-2xl font-bold text-yellow-800 mb-4 flex items-center">
              <TrophyIcon className="h-14 w-14 mr-2 text-yellow-600" />
              How are CashSNAP prizes distributed?
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              If your prediction is correct, you earn a share of the prize based on your adjusted activity score.
            </p>
            <div className="space-y-4 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600">
                  <ActivityIcon className="h-4 w-4" />
                </div>
                <div className="ml-3">
                  <p className="text-gray-700">
                    <span className="font-semibold">The more active you are</span> — and the earlier you vote — the bigger your share.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600">
                  <TrendingUpIcon className="h-4 w-4" />
                </div>
                <div className="ml-3">
                  <p className="text-gray-700">
                    <span className="font-semibold">Your final score</span> = Activity points × Vote timing bonus
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600">
                  <CoinsIcon className="h-4 w-4" />
                </div>
                <div className="ml-3">
                  <p className="text-gray-700">
                    <span className="font-semibold">Total prize</span> is split proportionally among all correct voters, based on their scores.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center my-8">
              <img 
                src="/images/cashsnaps/100.png" 
                alt="CashSNAP Prize Distribution" 
                className="rounded-lg shadow-md max-w-full h-auto"
              />
            </div>
          </section>

          {/* Activity Points Section */}
          <section>
            <h2 className="text-2xl font-bold text-yellow-800 mb-4 flex items-center">
              <BarChart3Icon className="h-14 w-16 mr-2 text-yellow-600" />
              Boost your chances with activity points by engaging on
            </h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <li className="flex items-center bg-yellow-50 p-3 rounded-lg">
                <CheckIcon className="h-5 w-5 text-yellow-600 mr-2" />
                <span>Vote, comment, like, or create SNAPs</span>
              </li>
              <li className="flex items-center bg-yellow-50 p-3 rounded-lg">
                <CheckIcon className="h-5 w-5 text-yellow-600 mr-2" />
                <span>Receive votes, comments & likes on your own SNAPs</span>
              </li>
            </ul>

            <div className="bg-white p-6 rounded-lg border border-yellow-200 shadow-sm">
              <h3 className="text-xl font-semibold text-yellow-800 mb-4">Activity Score System</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-yellow-700 mb-2">Direct</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600 mr-2">
                        <BarChart3Icon className="h-3 w-3" />
                      </div>
                      <span>Vote on a SNAP → <span className="font-semibold">+0.50 points</span></span>
                    </li>
                    <li className="flex items-center">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600 mr-2">
                        <HeartIcon className="h-3 w-3" />
                      </div>
                      <span>Like a SNAP → <span className="font-semibold">+0.25 points</span></span>
                    </li>
                    <li className="flex items-center">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600 mr-2">
                        <MessageSquareIcon className="h-3 w-3" />
                      </div>
                      <span>Comment on a SNAP → <span className="font-semibold">+0.25 points</span></span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-yellow-700 mb-2">Indirect (on SNAPs created by you)</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600 mr-2">
                        <BarChart3Icon className="h-3 w-3" />
                      </div>
                      <span>Vote on your SNAP → <span className="font-semibold">+0.50 points</span></span>
                    </li>
                    <li className="flex items-center">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600 mr-2">
                        <HeartIcon className="h-3 w-3" />
                      </div>
                      <span>Like on your SNAP → <span className="font-semibold">+0.25 points</span></span>
                    </li>
                    <li className="flex items-center">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600 mr-2">
                        <MessageSquareIcon className="h-3 w-3" />
                      </div>
                      <span>Comment on your SNAP → <span className="font-semibold">+0.25 points</span></span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-yellow-800 font-medium">
                  <span className="text-yellow-600 mr-1">★</span>
                  Early Adopter Bonus: 25% boost to all activity points during our launch phase!
                </p>
              </div>
            </div>

            <div className="flex justify-center my-8">
              <img 
                src="/images/cashsnaps/200.png" 
                alt="Activity Points Boost" 
                className="rounded-lg shadow-md max-w-full h-auto"
              />
            </div>
          </section>

          {/* Time Matters Section */}
          <section>
            <h2 className="text-2xl font-bold text-yellow-800 mb-4 flex items-center">
              <TrendingUpIcon className="h-6 w-6 mr-2 text-yellow-600" />
              Time matters!
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              Your activity score is multiplied by a bonus based on how early you vote!
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <p className="text-yellow-800 font-semibold">Early predictions = Higher reward potential</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <p className="text-yellow-800 font-semibold">Late predictions = Lower weight in the prize calculation</p>
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 mb-6">
              <div className="space-y-4">
                <div className="flex items-center">
                  <div>
                    <p className="text-gray-700">
                      <span className="font-semibold">Depending WHEN you vote on the lifetime of SNAP´s duration, your ACTIVITY-SCORE valuation CHANGES!</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-4 text-right mr-4">
                    <span className="font-semibold">•</span>
                  </div>
                  <div>
                    <p className="text-gray-700">If you vote in the first 10% → your score counts 100%</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-4 text-right mr-4">
                    <span className="font-semibold">•</span>
                  </div>
                  <div>
                    <p className="text-gray-700">If you vote between 10% and 30% → your score counts 75%</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-4 text-right mr-4">
                    <span className="font-semibold">•</span>
                  </div>
                  <div>
                    <p className="text-gray-700">If you vote between 30% and 60% → your score counts 50%</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-4 text-right mr-4">
                    <span className="font-semibold">•</span>
                  </div>
                  <div>
                    <p className="text-gray-700">If you vote between 60% and 90% → your score counts 25%</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-4 text-right mr-4">
                    <span className="font-semibold">•</span>
                  </div>
                  <div>
                    <p className="text-gray-700">If you vote in the last 10% → your score counts 5%</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center my-8">
              <img 
                src="/images/cashsnaps/300.png" 
                alt="Time Matters in CashSNAPs" 
                className="rounded-lg shadow-md max-w-full h-auto"
              />
            </div>
          </section>
          
          {/* General Terms Section */}
          <section>
            <h2 className="text-2xl font-bold text-yellow-800 mb-4">General Terms</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>SnapVote.ai reserves the right to modify or cancel CashSNAPs at any time.</li>
              <li>By participating, you agree to our <Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>.</li>
              <li>All decisions by SnapVote.ai regarding winners and prize amounts are final and binding.</li>
              <li>SnapVote.ai reserves the right to disqualify any participant suspected of fraud or abuse.</li>
              <li>Participants are responsible for any taxes applicable to their winnings.</li>
              <li>Prize distribution may be delayed or canceled in case of technical issues or force majeure.</li>
              <li>Identity verification through Stripe is required before any withdrawal of winnings.</li>
              <li>Verification will confirm your age (18+) and identity to comply with financial regulations.</li>
            </ul>
          </section>

          {/* No Payment Needed Section */}
          <section>
            <h2 className="text-2xl font-bold text-yellow-800 mb-4 flex items-center">
              <CoinsIcon className="h-6 w-6 mr-2 text-yellow-600" />
              No payment needed. No risk.
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              Just your prediction + participation = real rewards.
            </p>

            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 mb-6">
              <h3 className="text-xl font-semibold text-yellow-800 mb-4 flex items-center">
                <TrendingUpIcon className="h-5 w-5 mr-2 text-yellow-600" />
                How can this be free?
              </h3>
              <p className="text-gray-700 mb-4">
                Simple: We turn our marketing budget into prize money — for you.
              </p>
              <p className="text-gray-700 mb-4">
                Most new platforms spend a lot on paid ads to get attention. That money usually goes
                straight into the pockets of big social media companies.
              </p>
              <p className="text-gray-700 mb-4">
                We thought: What if we gave that money to our users instead?
              </p>
              <p className="text-gray-700 mb-4">
                So rather than paying tech giants for ads, we created CashSNAPs — prediction challenges
                where the marketing budget is spread directly among the community.
              </p>
              <p className="text-gray-700 mb-4">
                Every time you vote and participate, you help the platform grow — and if your prediction is
                correct, you get rewarded.
              </p>
              <p className="text-gray-700 font-semibold">
                No risk. No payment. Just your vote, your activity — and your chance to win real
                cash.
              </p>
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="primary"
              size="lg"
              className="bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500 px-8 py-3"
            >
              Find CashSNAPs Now
            </Button>
          </section>
        </div>
      </div>
    </div>
  );
}